/**
 * CR308-A/CR308-C: Web Recipe Override Section for EditSourceModal
 * Shows recipe dropdown (templates-first) + effective resolution preview.
 * CR308-C: Added "Create from Template" dialog for one-click override creation.
 * Only visible for WEB scan_type sources.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe, Info, ExternalLink, Loader2, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';

interface RecipeOption {
  id: string;
  recipe_name: string;
  recipe_config: Record<string, unknown>;
  is_template: boolean;
}

interface ScanSettingsRow {
  web_default_recipe_template_id: string | null;
  web_timeout_seconds: number;
}

interface WebRecipeOverrideSectionProps {
  sourceId: string;
  scanType: string;
  webRecipeTemplateId: string | null;
  onRecipeChange: (recipeId: string | null) => void;
  merchantName?: string | null;
  disabled?: boolean;
}

export function WebRecipeOverrideSection({
  sourceId,
  scanType,
  webRecipeTemplateId,
  onRecipeChange,
  merchantName,
  disabled = false,
}: WebRecipeOverrideSectionProps) {
  const { toast } = useToast();
  const [recipes, setRecipes] = useState<RecipeOption[]>([]);
  const [scanSettings, setScanSettings] = useState<ScanSettingsRow | null>(null);
  const [loading, setLoading] = useState(true);

  // CR308-C: Create-from-template dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [newRecipeName, setNewRecipeName] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch recipe options + S0 defaults in parallel (only for WEB sources)
  const loadData = useCallback(async () => {
    if (scanType !== 'WEB') return;
    setLoading(true);
    try {
      const [recipesRes, settingsRes] = await Promise.all([
        supabase
          .from('capture_recipe_templates')
          .select('id, recipe_name, recipe_config, is_template')
          .eq('recipe_scope', 'WEB_CRAWL')
          .eq('is_archived', false)
          .order('recipe_name'),
        supabase
          .from('app_scan_settings')
          .select('web_default_recipe_template_id, web_timeout_seconds')
          .eq('singleton_id', true)
          .maybeSingle(),
      ]);

      if (!recipesRes.error && recipesRes.data) {
        setRecipes(recipesRes.data as RecipeOption[]);
      }
      if (!settingsRes.error && settingsRes.data) {
        setScanSettings(settingsRes.data as ScanSettingsRow);
      }
    } catch (err) {
      console.error('[WebRecipeOverrideSection] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [scanType]);

  useEffect(() => { loadData(); }, [loadData]);

  // CR308-C: Split recipes into templates vs other
  const { templateRecipes, otherRecipes } = useMemo(() => {
    const tpl: RecipeOption[] = [];
    const other: RecipeOption[] = [];
    for (const r of recipes) {
      (r.is_template ? tpl : other).push(r);
    }
    return { templateRecipes: tpl, otherRecipes: other };
  }, [recipes]);

  // CR308-C: Auto-fill name when template changes
  const handleTemplateSelect = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    const tpl = recipes.find(r => r.id === templateId);
    if (tpl) {
      const merchant = merchantName || 'Merchant';
      setNewRecipeName(`${merchant} — Web Crawl (from ${tpl.recipe_name})`);
    }
  }, [recipes, merchantName]);

  // CR308-C: Create recipe from template + apply as override
  const handleCreateFromTemplate = useCallback(async () => {
    if (!selectedTemplateId || !newRecipeName.trim()) return;
    setCreating(true);
    try {
      const template = recipes.find(r => r.id === selectedTemplateId);
      if (!template) throw new Error('Template not found');

      // Clone template fields into new recipe
      const { data, error } = await supabase
        .from('capture_recipe_templates')
        .insert([{
          recipe_name: newRecipeName.trim(),
          recipe_scope: 'WEB_CRAWL',
          provider: 'firecrawl',
          recipe_config: template.recipe_config as never,
          is_system_template: false,
          is_template: false,
          is_archived: false,
          steps: [] as never,
          recipe_defaults: {} as never,
          success_criteria: {} as never,
          default_start_url: null,
        }])
        .select('id, recipe_name, recipe_config, is_template')
        .single();

      if (error) throw error;

      const newRecipe = data as RecipeOption;

      // Add to local list and apply as override
      setRecipes(prev => [...prev, newRecipe].sort((a, b) =>
        a.recipe_name.localeCompare(b.recipe_name)
      ));
      onRecipeChange(newRecipe.id);

      toast({
        title: 'Created + applied override',
        description: `"${newRecipe.recipe_name}" is now set as this source's override.`,
      });
      setShowCreateDialog(false);
    } catch (err) {
      console.error('[WebRecipeOverrideSection] Create error:', err);
      toast({
        title: 'Failed to create recipe',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [selectedTemplateId, newRecipeName, recipes, onRecipeChange, toast]);

  // Only show for WEB sources
  if (scanType !== 'WEB') return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading recipe options…
      </div>
    );
  }

  // Resolve effective recipe
  const sourceRecipe = recipes.find(r => r.id === webRecipeTemplateId);
  const s0DefaultRecipe = scanSettings?.web_default_recipe_template_id
    ? recipes.find(r => r.id === scanSettings.web_default_recipe_template_id)
    : null;

  // Resolve effective timeout
  let effectiveRecipe: { label: string; source: string } | null = null;
  let effectiveTimeout: { value: number; source: string };

  const parseRecipeTimeout = (config: Record<string, unknown>) => {
    // P2/CR307a: Read both old (timeout_ms) and new (timeout) keys
    const rawNew = (config as any)?.timeout;
    const rawOld = (config as any)?.timeout_ms;
    const ms = Number(rawNew) || Number(rawOld);
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  };

  if (sourceRecipe) {
    effectiveRecipe = { label: sourceRecipe.recipe_name, source: 'Source override' };
    const recipeTimeoutMs = parseRecipeTimeout(sourceRecipe.recipe_config);
    effectiveTimeout = recipeTimeoutMs
      ? { value: Math.round(recipeTimeoutMs / 1000), source: 'Recipe config' }
      : { value: scanSettings?.web_timeout_seconds ?? 120, source: 'S0 default' };
  } else if (s0DefaultRecipe) {
    effectiveRecipe = { label: s0DefaultRecipe.recipe_name, source: 'S0 default' };
    const recipeTimeoutMs = parseRecipeTimeout(s0DefaultRecipe.recipe_config);
    effectiveTimeout = recipeTimeoutMs
      ? { value: Math.round(recipeTimeoutMs / 1000), source: 'Recipe config' }
      : { value: scanSettings?.web_timeout_seconds ?? 120, source: 'S0 default' };
  } else {
    effectiveTimeout = { value: scanSettings?.web_timeout_seconds ?? 120, source: 'S0 default' };
  }

  return (
    <div className="grid gap-3 p-3 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          Web Scan Overrides
        </Label>
        <Link
          to="/admin/recipes"
          className="text-xs text-primary hover:underline flex items-center gap-1"
        >
          Recipes Studio
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {/* Recipe dropdown — templates-first */}
      <div className="grid gap-2">
        <Label className="text-sm flex items-center gap-1.5">
          Web Recipe (override)
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-xs">If set, this recipe is used instead of the S0 default.</p>
            </TooltipContent>
          </Tooltip>
        </Label>
        <Select
          value={webRecipeTemplateId ?? '__none__'}
          onValueChange={(v) => onRecipeChange(v === '__none__' ? null : v)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="None (use default)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None (use default)</SelectItem>
            {templateRecipes.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">Templates</SelectLabel>
                {templateRecipes.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.recipe_name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {otherRecipes.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">
                  {templateRecipes.length > 0 ? 'Other Recipes' : 'Recipes'}
                </SelectLabel>
                {otherRecipes.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.recipe_name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-between">
          {recipes.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No WEB_CRAWL recipes yet —{' '}
              <Link to="/admin/recipes" className="text-primary hover:underline">
                create one in Recipes Studio
              </Link>.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Source override → S0 default → heuristic crawl
            </p>
          )}

          {/* CR308-C: Create from template CTA */}
          {templateRecipes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs px-2 gap-1"
              onClick={() => {
                setSelectedTemplateId('');
                setNewRecipeName('');
                setShowCreateDialog(true);
              }}
              disabled={disabled}
            >
              <Plus className="h-3 w-3" />
              From template…
            </Button>
          )}
        </div>
      </div>

      {/* Effective Resolution Preview */}
      <div className="pt-2 border-t space-y-2">
        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Info className="h-3 w-3" />
          Effective Resolution (read-only)
        </Label>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2 bg-background rounded border">
            <span className="text-muted-foreground block mb-0.5">Effective Recipe</span>
            {effectiveRecipe ? (
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{effectiveRecipe.label}</span>
                <Badge variant="outline" className="text-[10px] px-1 py-0">
                  {effectiveRecipe.source}
                </Badge>
              </div>
            ) : (
              <span className="text-muted-foreground italic">Heuristic crawl (no recipe)</span>
            )}
          </div>

          <div className="p-2 bg-background rounded border">
            <span className="text-muted-foreground block mb-0.5">Effective Timeout</span>
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{effectiveTimeout.value}s</span>
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {effectiveTimeout.source}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* CR308-C: Create from Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Recipe from Template</DialogTitle>
            <DialogDescription>
              Clone a template's config into a new merchant-specific recipe and apply it as this source's override.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label className="text-sm">Template</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateSelect}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select a template…" />
                </SelectTrigger>
                <SelectContent>
                  {templateRecipes.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.recipe_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label className="text-sm">Recipe Name</Label>
              <Input
                value={newRecipeName}
                onChange={(e) => setNewRecipeName(e.target.value)}
                placeholder="e.g. Shoppers — Web Crawl (from Cookie Gate)"
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Auto-filled from merchant + template. Feel free to edit.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFromTemplate}
              disabled={creating || !selectedTemplateId || !newRecipeName.trim()}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Creating…
                </>
              ) : (
                'Create & Apply'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
